import { useEffect, useState } from "react";
import { fmtDate } from "@/lib/date";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/num";
import { Shell, Stat } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { MiniList, NotTracked, Panel } from "@/components/dashboard/Panels";
import { RolloverReminder } from "@/components/booklists/RolloverReminder";
import { useNavigate } from "react-router-dom";

const PurchaseBlocks = ({ b }) => (
  <>
    <Panel title="POs pending GRN" count={b.pos_pending_grn?.count} to="/receipts"
      testid="user-pos-pending-grn" footer="Goods receipt">
      <MiniList rows={b.pos_pending_grn?.rows} empty="Every PO is fully received."
        left={(r) => <><span className="mono font-bold">{r.po_no}</span>
          <span className="text-muted-foreground"> · {r.publisher}</span></>}
        right={(r) => r.pending_qty} />
    </Panel>
    <Panel title="Overdue POs" count={b.overdue_pos?.count} to="/purchase-orders"
      testid="user-overdue-pos" footer="Purchase orders">
      <MiniList rows={b.overdue_pos?.rows} empty="No PO is past its delivery date."
        left={(r) => <><span className="mono font-bold">{r.po_no}</span>
          <span className="text-muted-foreground"> · due {fmtDate(r.expected_date)}</span></>}
        right={(r) => r.pending_qty} />
    </Panel>
    <Panel title="Zero stock with pending demand" count={b.zero_stock_pending_demand?.count}
      to="/stock" testid="user-zero-stock" footer="Stock">
      <MiniList rows={b.zero_stock_pending_demand?.rows} empty="Nothing is out of stock."
        left={(r) => <><span className="font-medium">{r.title}</span>
          <span className="text-muted-foreground text-xs"> · {r.publisher}</span></>}
        right={(r) => `need ${r.pending_demand}`} />
    </Panel>
  </>
);

const WarehouseBlocks = ({ b }) => (
  <>
    <Panel title="Boxes ready to pack" count={b.boxes_ready_to_pack?.count} to="/my-work"
      testid="user-ready-to-pack" footer="My work">
      <MiniList rows={b.boxes_ready_to_pack?.rows} empty="No packing task is open."
        left={(r) => <><span className="mono font-bold">{r.task_no}</span>
          <span className="text-muted-foreground"> · {r.party_name}</span></>}
        right={(r) => r.bill_no || r.so_no || "—"} />
    </Panel>
    <Panel title="Bundling pending by school" count={b.bundling_pending?.count} to="/bundling"
      testid="user-bundling-pending" footer="Set bundling">
      <MiniList rows={b.bundling_pending?.rows} empty="Every class set is complete."
        left={(r) => <><span className="font-medium">{r.school_name}</span>
          <span className="text-muted-foreground"> · Cl {r.class_name}</span></>}
        right={(r) => r.pending_qty} />
    </Panel>
    <Panel title="Low stock titles" count={b.low_stock?.count} to="/stock"
      testid="user-low-stock" footer="Stock">
      <MiniList rows={b.low_stock?.rows} empty="Every title covers its demand."
        left={(r) => <><span className="font-medium">{r.title}</span>
          <span className="text-muted-foreground text-xs"> · free {r.free}</span></>}
        right={(r) => `short ${r.short_by}`} />
    </Panel>
    <Panel title="Pending stock reservations" count={b.stock_reservations?.count}
      to="/reallocation" testid="user-reservations" footer="Stock reallocation">
      <MiniList rows={b.stock_reservations?.rows} empty="Nothing is held."
        left={(r) => <span className="font-medium">{r.title}</span>}
        right={(r) => `${r.qty} held`} />
    </Panel>
  </>
);

const DispatchBlocks = ({ b }) => (
  <>
    <Panel title="Box IDs pending dispatch" count={b.box_ids_pending?.count} to="/shipments"
      testid="user-box-ids-pending" footer="Boxes">
      <MiniList rows={b.box_ids_pending?.rows} empty="Nothing is waiting to go out."
        left={(r) => <><span className="mono font-bold">{r.shipment_no}</span>
          <span className="text-muted-foreground"> · {r.party_name}</span></>}
        right={(r) => `${r.boxes} box(es)`} />
    </Panel>
    <Panel title="Batches dispatched today" count={b.batches_today?.count} to="/batches"
      testid="user-batches-today" footer="Courier batches">
      <MiniList rows={b.batches_today?.rows} empty="No batch has gone out today."
        left={(r) => <span className="mono font-bold">{r.batch_no}</span>}
        right={(r) => r.courier} />
    </Panel>
    <Panel title="POD pending" count={b.pod_pending?.count} to="/shipments"
      testid="user-pod-pending" footer="Boxes">
      <MiniList rows={b.pod_pending?.rows} empty="Every dispatched box has a POD."
        left={(r) => <><span className="mono font-bold">{r.shipment_no}</span>
          <span className="text-muted-foreground"> · {r.party_name}</span></>}
        right={(r) => fmtDate(r.dispatched_at)} />
    </Panel>
    <Panel title="Transporter-wise pending boxes" count={b.transporter_pending?.count}
      to="/shipments" testid="user-transporter-pending" footer="Boxes">
      <MiniList rows={b.transporter_pending?.rows} empty="Nothing pending."
        left={(r) => <span className="font-medium">{r.transporter}</span>}
        right={(r) => `${r.boxes} box(es)`} />
    </Panel>
  </>
);

const BillingBlocks = ({ b }) => (
  <>
    <Panel title="Invoices raised today" count={b.invoices_today?.count} to="/pending-billing"
      testid="user-invoices-today" footer="Bills">
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between border-b border-border pb-1.5">
          <span className="text-muted-foreground">Books billed</span>
          <span className="mono font-bold">{b.invoices_today?.qty ?? 0}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Value</span>
          <span className="mono font-bold" data-testid="invoices-today-amount">
            {fmtMoney(b.invoices_today?.amount ?? 0)}
          </span>
        </div>
      </div>
    </Panel>
    <Panel title="Unbilled sales orders" count={b.unbilled_sos?.count} to="/sales-orders"
      testid="user-unbilled-sos" footer="Sales orders">
      <MiniList rows={b.unbilled_sos?.rows} empty="Every order is billed."
        left={(r) => <><span className="mono font-bold">{r.so_no}</span>
          <span className="text-muted-foreground"> · {r.party_name}</span></>}
        right={(r) => r.balance_qty} />
    </Panel>
    <NotTracked testid="user-outstanding-payments" label="Outstanding payments by party"
      hint="Payments received are not recorded in the app yet." />
    <NotTracked testid="user-cod-pending" label="COD reconciliation pending"
      hint="COD collection is not recorded in the app yet." />
  </>
);

export default function UserDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard/user").then((r) => setD(r.data)).catch(() => {});
  }, []);

  const blocks = d?.blocks || {};
  const ready = d?.ready_to_bundle || {};

  return (
    <Shell title={`Good day, ${user?.name?.split(" ")[0] || ""}`}
      subtitle="Your work, shaped by the roles you hold.">
      <RolloverReminder onRollover={() => nav("/booklists")} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="user-top-cards">
        <Stat testid="user-ready-bundle" label="Schools Ready To Bundle" value={ready.count} tone="good" />
      </div>

      <Panel title="Class sets ready to bundle" to="/bundling" testid="user-ready-list"
        footer="Set bundling">
        <MiniList rows={ready.rows} empty="No class is fully covered yet."
          left={(r) => <><span className="font-medium">{r.school_name}</span>
            <span className="text-muted-foreground"> · Cl {r.class_name}</span></>}
          right={(r) => `${r.sets} sets`} />
      </Panel>

      {blocks.purchase && (
        <>
          <h2 className="text-base md:text-lg font-bold mt-8 mb-3">Purchase</h2>
          <div className="grid lg:grid-cols-3 gap-4" data-testid="block-purchase">
            <PurchaseBlocks b={blocks.purchase} />
          </div>
        </>
      )}

      {blocks.warehouse && (
        <>
          <h2 className="text-base md:text-lg font-bold mt-8 mb-3">Warehouse</h2>
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="block-warehouse">
            <WarehouseBlocks b={blocks.warehouse} />
          </div>
        </>
      )}

      {blocks.dispatch && (
        <>
          <h2 className="text-base md:text-lg font-bold mt-8 mb-3">Dispatch</h2>
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="block-dispatch">
            <DispatchBlocks b={blocks.dispatch} />
          </div>
        </>
      )}

      {blocks.billing && (
        <>
          <h2 className="text-base md:text-lg font-bold mt-8 mb-3">Billing</h2>
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="block-billing">
            <BillingBlocks b={blocks.billing} />
          </div>
        </>
      )}

      {d && !Object.keys(blocks).length && (
        <div className="empty-state mt-6" data-testid="no-role-blocks">
          No role-specific sections for your account yet.
        </div>
      )}
    </Shell>
  );
}
